import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, ArrowLeft, User, Upload, X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const AdminTeam = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    role: '',
    bio: '',
    image: '',
    email: '',
    order: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchMembers();
  }, [navigate]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team?active_only=false`);
      setMembers(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch team members",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Error",
        description: "Please upload a valid image file (JPEG, PNG, GIF, or WebP)",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image file size must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/uploads/image?image_type=team`,
        uploadFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Use full URL for the image
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData(prev => ({ ...prev, image: imageUrl }));
        
        // Show optimization info
        const reductionMsg = response.data.reduction_percent > 0 
          ? ` (optimized: ${response.data.reduction_percent}% smaller)`
          : '';
        toast({
          title: "Success",
          description: `Image uploaded successfully${reductionMsg}`
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingMember) {
        await axios.put(
          `${BACKEND_URL}/api/team/${editingMember.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Success", description: "Team member updated successfully" });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/team`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Success", description: "Team member added successfully" });
      }
      setShowForm(false);
      setEditingMember(null);
      resetForm();
      fetchMembers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save team member",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      title: member.title,
      role: member.role,
      bio: member.bio || '',
      image: member.image || '',
      email: member.email || '',
      order: member.order || 0
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/team/${memberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Success", description: "Team member deleted successfully" });
      fetchMembers();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete team member",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      role: '',
      bio: '',
      image: '',
      email: '',
      order: 0
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingMember(null);
    resetForm();
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {showForm ? (
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-team-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Team List
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {showForm ? (editingMember ? `Edit: ${editingMember.name}` : 'Add New Team Member') : 'Manage Team Members'}
            </h1>
          </div>
          {!showForm && (
            <Button
              onClick={() => {
                setShowForm(true);
                setEditingMember(null);
                resetForm();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-[#007A7A] hover:bg-[#007A7A]/90"
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="mb-8" data-testid="member-form">
            <CardHeader>
              <CardTitle>{editingMember ? 'Edit Team Member' : 'Add New Team Member'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="member-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="e.g., Executive Director"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="member-title-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                      placeholder="e.g., Leadership, Board Member, Staff"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="member-role-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="member-email-input"
                    />
                  </div>
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
                  <div className="flex items-start space-x-4">
                    {/* Preview */}
                    <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                      {formData.image ? (
                        <img src={resolveImageUrl(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-10 h-10 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 space-y-2">
                      {/* Upload Button */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          data-testid="member-image-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="border-[#007A7A] text-[#007A7A]"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {isUploading ? 'Uploading...' : 'Upload Photo'}
                        </Button>
                        {formData.image && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setFormData({ ...formData, image: '' })}
                            className="border-red-500 text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Max 5MB. Supported formats: JPEG, PNG, GIF, WebP</p>
                      
                      {/* Or Enter URL */}
                      <div className="pt-2">
                        <label className="block text-xs text-gray-500 mb-1">Or enter image URL:</label>
                        <input
                          type="text"
                          value={formData.image}
                          onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                          placeholder="https://example.com/photo.jpg"
                          data-testid="member-image-url-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="member-order-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    placeholder="Brief biography..."
                    data-testid="member-bio-input"
                  />
                </div>

                <div className="flex space-x-4">
                  <Button type="submit" className="bg-[#007A7A] hover:bg-[#007A7A]/90" data-testid="save-member-btn">
                    {editingMember ? 'Update Member' : 'Add Member'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-member-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="members-list">
            {members.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No team members yet. Click &quot;Add Team Member&quot; to add leadership and staff.
              </div>
            ) : (
              members.map((member) => (
                <Card key={member.id} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {member.image ? (
                          <img src={resolveImageUrl(member.image)} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{member.name}</h3>
                        <p className="text-sm text-[#007A7A] font-medium">{member.title}</p>
                        <p className="text-xs text-gray-500">{member.role}</p>
                        {member.bio && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{member.bio}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-4 pt-4 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleEdit(member)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        data-testid={`edit-member-${member.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(member.id)}
                        className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTeam;
